import { APP_ROUTES } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import type { Provider } from "@src/interfaces/provider.interface";
import { useCallback, useState } from "react";

export const useLocalCenterScheduling = () => {
  const { data, setData } = useApplicationContext();
  const navigate = useNavigateWithUTM();
  const [showProviderNotFoundModal, setShowProviderNotFoundModal] = useState(false);

  const isMeetTheTeamOrigin = data.providerSelectionSource === APP_ROUTES.MEET_THE_TEAM;

  const handleProviderSearchResult = useCallback(
    (
      filteredProviderList: Provider[],
      selectedProvider: Provider | null,
      isPrescriberSearch = false,
    ) => {
      if (!isMeetTheTeamOrigin) {
        return false;
      }

      const isSelectedProviderAvailable =
        !selectedProvider ||
        filteredProviderList.some((p) => p.providerId === selectedProvider.providerId);
      setData("isMeetTheTeamProviderAvailable", isSelectedProviderAvailable);

      if (!isSelectedProviderAvailable) {
        setShowProviderNotFoundModal(true);
        return true;
      }

      // Prescriber searches continue through the screener, which then routes to the provider profile.
      if (selectedProvider && isPrescriberSearch) {
        return false;
      }

      if (selectedProvider) {
        navigate(APP_ROUTES.PROVIDER_PROFILE);
        return true;
      }

      return false;
    },
    [isMeetTheTeamOrigin, navigate, setData],
  );

  const handleProviderNotFoundSeeMoreProviders = useCallback(
    (destination: string = APP_ROUTES.FIND_PROVIDER) => {
      setShowProviderNotFoundModal(false);
      navigate(destination);
    },
    [navigate],
  );

  const closeProviderNotFoundModal = useCallback(() => {
    setShowProviderNotFoundModal(false);
  }, []);

  return {
    isMeetTheTeamOrigin,
    showProviderNotFoundModal,
    handleProviderSearchResult,
    handleProviderNotFoundSeeMoreProviders,
    closeProviderNotFoundModal,
  };
};
